import React from 'react';
import { Wand2, Sparkles, Plus, Trash2 } from 'lucide-react';
import { X } from 'lucide-react';
import SecondaryPhotosPanel from './SecondaryPhotosPanel';
import CropPanel from './CropPanel';
import TranslatePanel from './TranslatingPanel';
import ReformatPanel from './ReformatPanel';
import RegionalEditPanel from './RegionalEditPanel';
import ShotSelector from './ShotSelector';
import BrandGuidelinesToggle from './BrandGuidelinesToggle';
import { ModelChoice } from './ModelChoice';
import { ALLOWED_TABS, tabIcons, IMAGE_CATEGORIES } from '../types';
import { SpinnerIcon } from './Spinner';

import { useCrop } from '../contexts/CropContext';
import { useRegionEdit } from '../contexts/RegionEditContext';
import { useMedia } from '../contexts/MediaContext';
import { useFeedback } from '../contexts/FeedbackContext';
import { useVideo } from '../contexts/VideoContext';
import { useEditor } from '../contexts/EditorContext';

const PanelArea: React.FC = () => {
  const { completedCrop, setAspect } = useCrop();
  const { isRegionSelectMode, regionAspect, setRegionAspect, editRegion, handleClearRegion, setRegionCrop, setRegionCompletedCrop } = useRegionEdit();
  const { secondaryImages, handleSecondaryUpload, handleSecondaryRemove, handleSecondaryCategoryChange } = useMedia();
  const { isFeedbackPanelExpanded } = useFeedback();
  const { shotsPrompts, setShotsPrompts } = useVideo();
  const {
    prompts, setPrompts, editModel, setEditModel,
    isLoading, isSubmitting, activeTab, setActiveTab,
    currentImage,
    handleGenerate, handleAutoTranslate, handleManualTranslate,
    handleReformat, handleApplyCrop, handleApplyAICrop,
    handleToggleRegionMode, handleClearHotspot,
    editHotspot, onHoverCombo,
  } = useEditor();

  const modelChoice = <ModelChoice editModel={editModel} setEditModel={setEditModel} isLoading={isLoading} />;
  const regionalEditActive = isRegionSelectMode || !!editRegion;

  const MAX_PROMPTS = 5;
  const hasNonEmptyPrompt = prompts.some(p => p.trim());

  return (
    <div
      className={`flex flex-col gap-3 overflow-hidden transition-all duration-300 ease-in-out ${
        isFeedbackPanelExpanded ? 'w-0 opacity-0' : 'w-[40%] opacity-100'
      }`}
    >
      {/* Tabs - 2 columns with clean styling */}


        <div className="grid grid-cols-4 gap-1 mb-4">
          {ALLOWED_TABS.map(tab => {
            const Icon = tabIcons[tab];
            const isBeta = tab === 'Reformat';
            return (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`
                  text-xs capitalize font-medium py-2 px-3 rounded-lg
                  transition-colors duration-200 whitespace-nowrap
                  ${activeTab === tab
                    ? 'bg-gray-800/60 text-white border border-gray-700/50'
                    : 'text-gray-400 hover:text-gray-300 hover:bg-gray-800/40 border border-transparent'
                  }
                `}
              >
                <span className="relative inline-flex items-center justify-center gap-2 w-full overflow-visible">
                  <Icon className="w-3.5 h-3.5" />
                  {tab}
                  {isBeta && (
                    <span className="absolute -top-2 -left-2 bg-gradient-to-r from-blue-500 to-purple-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full shadow-lg whitespace-nowrap">
                      Beta
                    </span>
                  )}
                </span>
              </button>
            );
          })}
        </div>

        {/* Tab-specific Controls - Scrollable */}
        <div className="flex-1 flex flex-col gap-3 bg-gray-800/60 backdrop-blur-sm rounded-xl p-4 shadow-lg border border-gray-700/50 overflow-y-auto">
          {activeTab === 'Editing' && (
              <>
                  <div className="space-y-3">
                    <BrandGuidelinesToggle category="creative" />
                    {!regionalEditActive && modelChoice}

                    {/* Instructions Banner */}
                    <div className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 backdrop-blur-xl border border-blue-500/20 rounded-xl p-4">
                      <div className="flex items-start gap-3">
                        <Wand2 className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-200 mb-1">How to edit</p>
                          <p className="text-xs text-gray-400 leading-relaxed">
                            {isRegionSelectMode
                              ? 'Click and drag on the image to select a region to edit.'
                              : 'Click an area on the image to make a precise edit, or describe your changes below.'}
                          </p>
                        </div>
                        {(editHotspot) && (
                          <button
                            onClick={handleClearHotspot}
                            className="flex-shrink-0 p-1.5 rounded-lg bg-red-500/20 hover:bg-red-500/30 border border-red-500/30 hover:border-red-500/40 text-red-400 hover:text-red-300 transition-all duration-200 hover:scale-110 active:scale-95 group"
                            aria-label={`Clear hotspot`}
                          >
                            <X className="w-4 h-4 group-hover:rotate-90 transition-transform duration-200" />
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Regional Editing Toggle */}
                    <RegionalEditPanel
                      isRegionSelectMode={isRegionSelectMode}
                      onToggleRegionMode={handleToggleRegionMode}
                      editRegion={editRegion}
                      onClearRegion={handleClearRegion}
                      isLoading={isLoading}
                      regionAspect={regionAspect}
                      onSetRegionAspect={setRegionAspect}
                      onClearRegionCrop={() => { setRegionCrop(undefined); setRegionCompletedCrop(undefined); }}
                    />

                    <SecondaryPhotosPanel
                      images={secondaryImages}
                      onUpload={handleSecondaryUpload}
                      onRemove={handleSecondaryRemove}
                      onCategoryChange={handleSecondaryCategoryChange}
                      accept={['image/jpeg', 'image/png', 'image/webp']}
                      maxFiles={12}
                      maxSizeMB={8}
                      title="Reference Photos"
                      subtitle="Add photos as references"
                      cta="Add photos"
                      categories={IMAGE_CATEGORIES}
                    />
                  </div>

                  <div className="pb-3 border-b border-gray-700/50">
                    <ShotSelector selectedPrompts={shotsPrompts} setSelectedPrompts={setShotsPrompts}/>
                  </div>

                  <div className="bg-gray-800/40 backdrop-blur-xl border border-gray-700/50 rounded-xl p-4 shadow-lg">
                  <div className="space-y-3">
                    <label className="flex items-center justify-between text-sm font-semibold text-gray-200">
                      <span className="flex items-center gap-2">
                        <Wand2 className="w-4 h-4 text-blue-400" />
                        {prompts.length > 1 ? `Your Prompts (${prompts.length})` : 'Your Prompt'}
                      </span>
                      {prompts.length < MAX_PROMPTS && (
                        <button
                          onClick={() => setPrompts(prev => [...prev, ''])}
                          disabled={isLoading}
                          className="flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300 disabled:text-gray-600 disabled:cursor-not-allowed transition-colors"
                        >
                          <Plus className="w-3.5 h-3.5" />
                          Add prompt
                        </button>
                      )}
                    </label>
                    {prompts.map((p, index) => (
                      <div key={index} className="relative group">
                        <textarea
                          value={p}
                          onChange={(e) => setPrompts(prev => prev.map((old, i) => i === index ? e.target.value : old))}
                          placeholder={prompts.length > 1 ? `Prompt ${index + 1}...` : 'Describe the changes you want to make...'}
                          className="w-full bg-gray-900/50 backdrop-blur-sm border border-gray-700/50 text-gray-200 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 focus:outline-none transition disabled:cursor-not-allowed disabled:opacity-60 resize-none placeholder:text-gray-500"
                          disabled={isLoading}
                          rows={3}
                        />
                        {prompts.length > 1 && (
                          <button
                            onClick={() => setPrompts(prev => prev.filter((_, i) => i !== index))}
                            disabled={isLoading}
                            className="absolute top-2 right-2 p-1 rounded-md bg-red-500/20 hover:bg-red-500/30 border border-red-500/30 text-red-400 hover:text-red-300 opacity-0 group-hover:opacity-100 transition-all disabled:cursor-not-allowed"
                            aria-label={`Remove prompt ${index + 1}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      onClick={handleGenerate}
                      className="w-full bg-gradient-to-r from-blue-600 via-blue-500 to-purple-600 text-white font-semibold py-3 px-4 text-sm rounded-lg transition-all duration-300 ease-out shadow-lg shadow-blue-500/25 hover:shadow-xl hover:shadow-blue-500/40 hover:scale-[1.02] active:scale-[0.98] disabled:from-gray-700 disabled:to-gray-600 disabled:shadow-none disabled:cursor-not-allowed disabled:scale-100 relative overflow-hidden group"
                      disabled={isLoading || isSubmitting || !hasNonEmptyPrompt || !currentImage}
                    >
                      <span className="relative z-10 flex items-center justify-center gap-2">
                        {isSubmitting ? (
                          <>
                            <SpinnerIcon />
                            Submitting...
                          </>
                        ) : (
                          <>
                            <Sparkles className="w-4 h-4" />
                            {prompts.filter(p => p.trim()).length > 1
                              ? `Generate ${prompts.filter(p => p.trim()).length} Edits`
                              : 'Generate'}
                          </>
                        )}
                      </span>
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000" />
                    </button>
                  </div>
                </div>
              </>
          )}
          {activeTab === 'Translating' &&
            <div className="space-y-3">
              {modelChoice}
              <TranslatePanel
                handleAutoTranslate={handleAutoTranslate}
                handleManualTranslate={handleManualTranslate}
                isLoading={isLoading}
                isSubmitting={isSubmitting}
                hasImage={!!currentImage}
                isRegionSelectMode={isRegionSelectMode}
                onToggleRegionMode={handleToggleRegionMode}
                editRegion={editRegion}
                onClearRegion={handleClearRegion}
                regionAspect={regionAspect}
                onSetRegionAspect={setRegionAspect}
                onClearRegionCrop={() => { setRegionCrop(undefined); setRegionCompletedCrop(undefined); }}
              />
            </div>
          }
          {activeTab === 'Reformat' &&
            <div className="flex flex-col h-full gap-3">
              <BrandGuidelinesToggle category="layout" hideAssets />
              <div className="flex flex-col flex-1 min-h-0">
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Aspect Ratio</h3>
                <ReformatPanel
                  onReformat={handleReformat}
                  onHoverCombo={onHoverCombo}
                  isLoading={isLoading}
                  isSubmitting={isSubmitting}
                  hasImage={!!currentImage}
                />
              </div>
            </div>
          }
          {activeTab === 'Cropping' &&
            <div className="space-y-3">
              <CropPanel onApplyCrop={handleApplyCrop} onApplyAICrop={handleApplyAICrop} onSetAspect={setAspect} isLoading={isLoading} isSubmitting={isSubmitting} isCropping={!!completedCrop?.width && completedCrop.width > 0} hasImage={!!currentImage} />
            </div>
          }
        </div>
    </div>
  );
};

export default PanelArea;
